/**
 * What Changed Card — recent signals in the last 30 days
 *
 * Shows a list of material changes with severity badges.
 * Empty state when no signals detected.
 */

import type { RecentSignal, SignalSeverity, SignalType } from '../../../types';

interface WhatChangedCardProps {
  signals: RecentSignal[];
}

function SignalIcon({ type }: { type: SignalType }) {
  const icons: Record<SignalType, string> = {
    news: '\ud83d\udcf0',
    executive_change: '\ud83d\udc64',
    job_spike: '\ud83d\udcca',
    customer_change: '\ud83e\udd1d',
    financial: '\ud83d\udcb0',
  };

  return (
    <span className="text-sm" aria-hidden="true">
      {icons[type] || '\u2022'}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: SignalSeverity }) {
  const config: Record<SignalSeverity, { label: string; bg: string; text: string }> = {
    high: { label: 'High', bg: 'bg-red-100', text: 'text-red-800' },
    medium: { label: 'Medium', bg: 'bg-yellow-100', text: 'text-yellow-800' },
    low: { label: 'Low', bg: 'bg-gray-100', text: 'text-gray-600' },
  };

  const { label, bg, text } = config[severity] || config.medium;

  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${bg} ${text}`}
      data-testid="severity-badge"
    >
      {label}
    </span>
  );
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export function WhatChangedCard({ signals }: WhatChangedCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid="what-changed-card">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">
        What Changed
        <span className="ml-2 text-xs font-normal text-gray-400">Last 30 Days</span>
      </h3>

      {signals.length === 0 ? (
        <div className="text-center py-6" data-testid="empty-signals">
          <p className="text-sm text-gray-500">No significant changes detected</p>
          <p className="text-xs text-gray-400 mt-1">
            Monitoring news, leadership, hiring, and customer activity
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {signals.map((signal, i) => (
            <div
              key={`${signal.title}-${i}`}
              className="flex items-start gap-3 pb-3 border-b border-gray-50 last:border-0 last:pb-0"
              data-testid="signal-item"
            >
              <SignalIcon type={signal.type} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900 leading-tight">
                    {signal.title}
                  </span>
                  <SeverityBadge severity={signal.severity} />
                </div>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{signal.description}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400">{formatDate(signal.date)}</span>
                  {signal.source && (
                    <>
                      <span className="text-gray-300">-</span>
                      <span className="text-xs text-gray-400">{signal.source}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
